import _mapValues from "lodash/mapValues";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { fetchDataSourceStateById } from "src/requests/dataSources/requestCreators";
import { isEmpty } from "src/utils/dictionary";
import {
  getByIdDataSource,
  getByIdDataSources,
  getByIdDataSourceStateSlice,
  getDataSourcesDataById,
  getIsDataSourcesFetching,
  getIsDataSourceStateFetchingForId,
  getIsStateFetching,
} from "./selectors";
import { DataSourceModelDict, OnlyStateDic, StateReducerProps } from "./types";
import { useFetchDataSources, useFetchDataSourceState } from "./useActions";

export const useIsDataSourcesFetching = () =>
  useSelector((state) => getIsDataSourcesFetching(state));

const useDataSourcesByIdSelector = () =>
  useSelector((state) => getByIdDataSources(state));

const useDataSourcesDataById = () => useSelector(getDataSourcesDataById);

const useNeedToFetchDatasoruces = () => {
  const dataSourcesData = useDataSourcesDataById();
  return (
    isEmpty(dataSourcesData) || Object.values(dataSourcesData).some(isEmpty)
  );
};

export const useDataSourcesDataByIdAutofetch = (): DataSourceModelDict => {
  const needToFetch = useNeedToFetchDatasoruces();
  const dataSources = useDataSourcesDataById();
  const isFetching = useIsDataSourcesFetching();
  const fetchDataSources = useFetchDataSources();

  useEffect(() => {
    if (needToFetch && !isFetching) {
      fetchDataSources();
    }
  }, [needToFetch, fetchDataSources, isFetching]);

  return dataSources;
};

export const useDataSourceById = (dataSourceId: number | null) =>
  useSelector((state) => getByIdDataSource(state, dataSourceId));

export const useDataSourceBydIdAUTOFETCH = (dataSourceId: number | null) => {
  const dataSource = useDataSourceById(dataSourceId);
  const fetchDataSources = useFetchDataSources();

  useEffect(() => {
    if (!dataSourceId || !isEmpty(dataSource ?? {})) return;
    fetchDataSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceId]);

  return dataSource;
};

export const useDataSourcesState = (): OnlyStateDic => {
  const byIdSlice = useDataSourcesByIdSelector();
  const fetchDataSourcesState = useFetchDataSourceState();

  const noneState = useMemo(
    () =>
      !!Object.values(byIdSlice).filter(
        (dataSource) => dataSource.state === undefined
      ).length,
    [byIdSlice]
  );

  const fetchStateQueuryFetch = () => {
    Object.keys(byIdSlice).forEach((key) => {
      fetchDataSourcesState(Number(key));
    });
  };

  useEffect(() => {
    if (noneState) {
      fetchStateQueuryFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDataSourcesState, noneState]);

  const onlyStateDic: OnlyStateDic = {};

  Object.keys(byIdSlice).forEach((key) => {
    onlyStateDic[Number(key)] = byIdSlice[Number(key)].state;
  });

  return onlyStateDic;
};

export const useDataSourcesStateLength = (): number => {
  const dataSourcesStates = useDataSourcesState();
  return Object.keys(dataSourcesStates).length;
};

export const useDataSourceStateById = (
  dataSourceId: number
  // eslint-disable-next-line consistent-return
): StateReducerProps | undefined => {
  const dataSourceState = useSelector((state) =>
    getByIdDataSourceStateSlice(state, dataSourceId)
  );

  const [fetchedData, setFetchedData] = useState<StateReducerProps | null>(
    null
  );

  const fetch = async () => {
    const data = (await fetchDataSourceStateById(
      dataSourceId
    )) as unknown as StateReducerProps;

    setFetchedData(data);
  };

  useEffect(() => {
    if (!dataSourceState) {
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceState]);

  if (fetchedData || dataSourceState) return fetchedData ?? dataSourceState;
};

export const useIsDataSourcesStateFetching = () =>
  useSelector((state) => getIsStateFetching(state));

export const useIsDataSourceStateFetching = (dataSourceId: number) =>
  useSelector((state) =>
    getIsDataSourceStateFetchingForId(state, dataSourceId)
  );
